import { useState } from "react";
import { MapPin, Phone, Clock, ArrowUpRight, CheckCircle2, ArrowRight } from "lucide-react";
import { Reveal } from "../lib/motion";
import { MascotWelcome } from "../components/Mascot";
import { useRequestDialog } from "../components/RequestDialog";
import { usePageMeta } from "../lib/seo";
import { api } from "../lib/api";
import { ABOUT_DEFAULT, BUSINESS, CONTACT_DEFAULT, DEALERS_DEFAULT } from "../content/defaults";

function PageHead({ eyebrow, title, children }) {
  return (
    <section className="hero" style={{ paddingBottom: 10 }}>
      <div className="container" style={{ maxWidth: 860 }}>
        <Reveal>
          <span className="eyebrow red">{eyebrow}</span>
          <h1 style={{ fontSize: "clamp(30px,4.4vw,54px)", lineHeight: 1.05, margin: "16px 0 18px" }} data-testid="static-page-heading">{title}</h1>
          {children}
        </Reveal>
      </div>
    </section>
  );
}

export function About() {
  const openDialog = useRequestDialog();
  usePageMeta("About C.C.C. Auto Electric | Hawaiian Gardens Auto Electrical Shop",
    "A neighborhood automotive electrical shop on Carson Street in Hawaiian Gardens. Read what customers say, then tell us what your vehicle is doing.");
  return (
    <main>
      <PageHead eyebrow="About the shop" title={ABOUT_DEFAULT.heading}>
        {ABOUT_DEFAULT.paragraphs.map((p, i) => <p key={i} className="hero-sub">{p}</p>)}
      </PageHead>
      <section className="section" style={{ paddingTop: 30 }}>
        <div className="container">
          <div className="grid-2" style={{ alignItems: "center" }}>
            <Reveal>
              <img src="/assets/ccc-mechanic-mascot.png" alt="The C.C.C. Auto Electric mascot" className="mascot-bob" style={{ width: "100%", maxWidth: 380, margin: "0 auto", display: "block" }} />
            </Reveal>
            <Reveal delay={0.1}>
              <div className="card" style={{ padding: 30 }}>
                <h3 style={{ fontSize: 19, marginBottom: 10 }}>How we work</h3>
                <p className="muted" style={{ lineHeight: 1.75, fontSize: 14.5 }}>{ABOUT_DEFAULT.mascot_note}</p>
                <ul className="symptoms" style={{ margin: "18px 0 22px" }}>
                  <li>Symptoms first — no parts knowledge required to ask for help</li>
                  <li>Request online; the shop confirms availability by phone or email</li>
                  <li>Payment is handled at our in-store register</li>
                </ul>
                <button className="btn btn-blue" onClick={() => openDialog("Electrical diagnosis", "", true)} data-testid="about-request-cta">Request an Appointment</button>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}

export function Contact() {
  const openDialog = useRequestDialog();
  usePageMeta("Contact & Location | C.C.C. Auto Electric, Hawaiian Gardens CA",
    "Call (562) 425-2027 or send a repair request online. 12430 E Carson St, Hawaiian Gardens, CA 90716.");
  return (
    <main>
      <PageHead eyebrow="Contact & location" title={CONTACT_DEFAULT.heading}>
        <p className="hero-sub">{CONTACT_DEFAULT.text}</p>
      </PageHead>
      <section className="section" style={{ paddingTop: 30 }}>
        <div className="container grid-2">
          <Reveal>
            <div className="card" style={{ padding: 30, display: "grid", gap: 18 }}>
              <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                <Phone size={18} style={{ color: "var(--red-bright)", flex: "none", marginTop: 2 }} />
                <div>
                  <strong style={{ fontFamily: "var(--font-display)" }}>Call the shop</strong>
                  <p className="muted" style={{ margin: "4px 0 0" }}><a href={BUSINESS.phoneHref} style={{ color: "var(--blue-bright)" }} data-testid="contact-phone-link">{BUSINESS.phoneDisplay}</a></p>
                </div>
              </div>
              <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                <MapPin size={18} style={{ color: "var(--red-bright)", flex: "none", marginTop: 2 }} />
                <div>
                  <strong style={{ fontFamily: "var(--font-display)" }}>Visit</strong>
                  <p className="muted" style={{ margin: "4px 0 0" }}>{BUSINESS.address}</p>
                </div>
              </div>
              <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                <Clock size={18} style={{ color: "var(--red-bright)", flex: "none", marginTop: 2 }} />
                <div>
                  <strong style={{ fontFamily: "var(--font-display)" }}>Hours</strong>
                  <p className="muted" style={{ margin: "4px 0 0" }}>Call for current hours.</p>
                </div>
              </div>
              <button className="btn btn-red" onClick={() => openDialog("Electrical diagnosis", "", true)} data-testid="contact-request-cta">Send a Repair Request</button>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="card" style={{ overflow: "hidden", minHeight: 360 }}>
              <iframe title="Map to C.C.C. Auto Electric"
                src="https://www.google.com/maps?q=12430+E+Carson+St,+Hawaiian+Gardens,+CA+90716&output=embed"
                width="100%" height="100%" style={{ border: 0, minHeight: 360 }} loading="lazy" data-testid="contact-map" />
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}

export function Privacy() {
  usePageMeta("Privacy Policy | C.C.C. Auto Electric",
    "How the C.C.C. Auto Electric website handles your repair request details, customer access links, dealer referrals and payments.");
  return (
    <main>
      <PageHead eyebrow="Privacy" title="Privacy policy">
        <p className="hero-sub">Updated October 7, 2026. Plain-language summary of how this website handles information.</p>
      </PageHead>
      <section className="section" style={{ paddingTop: 24 }}>
        <div className="container" style={{ maxWidth: 820, display: "grid", gap: 18 }}>
          {[
            ["What we collect", "Repair requests store the contact and vehicle details you enter (name, phone, email, vehicle, symptoms). The chat stores your messages so staff can review the conversation."],
            ["Who sees it", "Only shop staff. Customer access links show one ticket at a time and expire after fourteen days. Inspection photos and videos stay private until staff share a finding with you."],
            ["Dealers", "Dealer partners only see the referral details a customer authorizes — never repair history, invoices or private notes."],
            ["Payments", "There are no online payments. Card transactions happen in person at the register."],
            ["Contact", "Questions about your information? Call (562) 425-2027 or visit the shop."],
          ].map(([h, b], i) => (
            <Reveal key={i} delay={i * 0.05}>
              <div className="card" style={{ padding: 24 }}>
                <h3 style={{ fontSize: 17, marginBottom: 8 }}>{h}</h3>
                <p className="muted" style={{ margin: 0, lineHeight: 1.7, fontSize: 14.5 }}>{b}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </main>
  );
}

export function Dealers() {
  usePageMeta("Dealer & Fleet Partners | C.C.C. Auto Electric",
    "Dealerships and fleets refer customers with electrical concerns. Approved partners receive a private referral portal.");
  const [form, setForm] = useState({ dealership_name: "", business_address: "", contact_name: "", work_email: "", phone: "", website: "", dealer_license: "", referral_types: "", preferred_contact: "Email", consent: false });
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await api("/dealers/apply", { method: "POST", body: form });
      setDone(res.dealer_reference);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main>
      <PageHead eyebrow="Dealer & fleet partners" title={DEALERS_DEFAULT.heading}>
        <p className="hero-sub">{DEALERS_DEFAULT.text}</p>
      </PageHead>
      <section className="section" style={{ paddingTop: 30 }}>
        <div className="container" style={{ maxWidth: 720 }}>
          <Reveal>
            <div className="card" style={{ padding: 30 }}>
              {done ? (
                <div className="ok-box" data-testid="dealer-apply-success">
                  <CheckCircle2 size={16} style={{ verticalAlign: -3, marginRight: 6 }} />
                  <strong>Application {done} received.</strong> Shop staff review registrations and will contact you. Approved partners receive a private portal link by phone or email.
                </div>
              ) : (
                <>
                  <h3 style={{ fontSize: 19, marginBottom: 6 }}>Apply for the referral program</h3>
                  <p className="form-note" style={{ marginBottom: 20 }}>Shop admin approves registrations; no automatic preferred status or commissions.</p>
                  <form onSubmit={submit} data-testid="dealer-apply-form">
                    <div className="field-pair">
                      <div className="field"><label>Dealership name</label><input required value={form.dealership_name} onChange={set("dealership_name")} data-testid="dealer-name-input" /></div>
                      <div className="field"><label>Contact name</label><input required value={form.contact_name} onChange={set("contact_name")} data-testid="dealer-contact-input" /></div>
                    </div>
                    <div className="field"><label>Business address</label><input required value={form.business_address} onChange={set("business_address")} /></div>
                    <div className="field-pair">
                      <div className="field"><label>Work email</label><input required type="email" value={form.work_email} onChange={set("work_email")} data-testid="dealer-email-input" /></div>
                      <div className="field"><label>Phone</label><input required type="tel" value={form.phone} onChange={set("phone")} /></div>
                    </div>
                    <div className="field-pair">
                      <div className="field"><label>Website (optional)</label><input value={form.website} onChange={set("website")} /></div>
                      <div className="field"><label>Dealer license (optional)</label><input value={form.dealer_license} onChange={set("dealer_license")} /></div>
                    </div>
                    <div className="field"><label>What do you want to refer? (electrical diagnostics, no-starts, fleet vehicles…)</label><textarea required value={form.referral_types} onChange={set("referral_types")} rows={3} data-testid="dealer-referral-types-input" /></div>
                    <div className="field"><label>Preferred contact</label>
                      <select value={form.preferred_contact} onChange={set("preferred_contact")}><option>Email</option><option>Phone</option></select>
                    </div>
                    <label className="consent">
                      <input type="checkbox" required checked={form.consent} onChange={set("consent")} data-testid="dealer-consent-checkbox" />
                      <span>Authorize C.C.C. Auto Electric to contact me about this application and future referrals.</span>
                    </label>
                    {error && <div className="error-box" role="alert" data-testid="dealer-apply-error">{error}</div>}
                    <button className="btn btn-blue" disabled={busy} data-testid="dealer-apply-submit">{busy ? "Sending…" : "Submit Application"}</button>
                  </form>
                </>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
